import { useTranslation } from "react-i18next";

import { MediaTypeImage } from "@components/mediaTypeImage";
import { CONTENT_TYPE, CONTENTS_PREVIEW_STATUS } from "@constants";
import { Box, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { alpha } from "@mui/material";
import { IconViewDetails } from "@mzc-cloudplex/icons";
import { useMemo } from "react";
import useThumbnailCrop from "@hooks/useThumbnailCrop";
import ImagePreviewV2 from "@components/modals/image-preview-v2";

const ContentMediaImage = ({
    contentType,
    variant,
    size,
    errorIconProps,
    data,
    draggable,
    isSelected,
    isShowImagePreviewModal,
    handleShowImagePreviewModal,
    handleCloseImagePreviewModal,
}) => {
    const { t } = useTranslation();

    const commonStatusPreviews = data.previews?.filter((p) => p.status !== CONTENTS_PREVIEW_STATUS.ERROR);
    let src = commonStatusPreviews?.[0]?.accessUrl;
    if (!src) {
        if (contentType === CONTENT_TYPE.VIDEO) src = data.primaryPoster?.accessUrl;
        else if ([CONTENT_TYPE.PEOPLE, CONTENT_TYPE.MUSIC, CONTENT_TYPE.PHOTO].includes(contentType))
            src = data.primaryImage?.accessUrl;
        else src = data.accessUrl;
    }

    const previewStatus = useMemo(() => {
        // NOTE : 모든 프리뷰가 Error일 경우에만 Error로 표시
        if (commonStatusPreviews?.length === 0 && data.previews?.length > 0) return CONTENTS_PREVIEW_STATUS.ERROR;
        else return commonStatusPreviews?.[0]?.status ?? CONTENTS_PREVIEW_STATUS.COMPLETE;
    }, [data]);

    const { previewContainerStyle, previewImageStyle } = useThumbnailCrop({ variant });

    const previewModalSrc = useMemo(() => {
        return commonStatusPreviews?.[commonStatusPreviews?.length - 1]?.accessUrl;
    }, [commonStatusPreviews]);

    return (
        <Box
            draggable={draggable}
            sx={{
                position: `relative`,
                ".btn-preview": {
                    opacity: 0,
                },
                "&:hover .btn-preview": {
                    opacity: 1,
                },
                ...(isSelected && {
                    backgroundColor: `${alpha(theme.palette.greySecondary.A100, 0.6)} !important`,
                }),
                ...previewContainerStyle,
            }}
        >
            <MediaTypeImage
                mediaType={contentType}
                state={data.state}
                src={src}
                size={size}
                errorIconProps={errorIconProps}
                previewStatus={previewStatus}
                draggable={draggable}
                variant={variant}
                sx={previewImageStyle}
                width={variant === "table" ? 28 : 46}
                height={variant === "table" ? 28 : 56}
            />
            {src &&
                previewStatus === CONTENTS_PREVIEW_STATUS.COMPLETE &&
                isShowImagePreviewModal !== undefined &&
                handleShowImagePreviewModal && (
                    <>
                        <Box
                            className={"btn-preview"}
                            role="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleShowImagePreviewModal();
                            }}
                            gap={1}
                            sx={{
                                opacity: 1,
                                position: `absolute`,
                                left: `50%`,
                                top: `50%`,
                                display: `flex`,
                                flexDirection: `column`,
                                alignItems: `center`,
                                justifyContent: `center`,
                                width: variant === "table" ? 32 : 100,
                                height: variant === "table" ? 32 : 100,
                                margin: variant === "table" ? `-16px 0 0 -16px` : `-50px 0 0 -50px`,
                                color: theme.palette.common.white,
                                backgroundColor: alpha(theme.palette.constant.black, 0.6),
                                borderRadius: `100%`,
                                transition: `opacity 0.2s linear`,
                                cursor: `pointer`,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 16,
                                    height: 16,
                                    display: `flex`,
                                    alignItems: `center`,
                                    justifyContent: `center`,
                                    borderRadius: `50%`,
                                    color: theme.palette.constant.white,
                                    backgroundColor: theme.palette.etc.gray500,
                                }}
                            >
                                <IconViewDetails size={20} />
                            </Box>
                            {variant !== "table" && (
                                <Typography variant={"body4_500"} color={theme.palette.constant.white}>
                                    {t(`common::label::Image`)} {t(`common::label::Zoom in`)}
                                </Typography>
                            )}
                        </Box>
                    </>
                )}

            {isShowImagePreviewModal && (
                <ImagePreviewV2 asset={data} onClickClose={handleCloseImagePreviewModal} src={previewModalSrc} />
            )}
        </Box>
    );
};

export default ContentMediaImage;
